import { app } from '$stores/app.svelte';
import { install } from '$stores/install.svelte';

export interface DesktopUpdateState {
	phase: string;
	version?: string;
	seconds?: number;
	error?: string;
}

export interface DesktopBridge {
	getInfo(): Promise<{
		version: string;
		autostart: boolean;
		update?: DesktopUpdateState | null;
	} | null>;
	checkUpdate(): Promise<DesktopUpdateState | null>;
	setAutostart(enabled: boolean): Promise<boolean>;
	reportBusy(busy: boolean): void;
	readyToRestart(ready: boolean): void;
	/** `silent` понимает оболочка с 1.0.8; прежние показывают уведомление со звуком Windows. */
	notify(payload: { title: string; body: string; path?: string; silent?: boolean }): void;
	onUpdate(callback: (state: DesktopUpdateState) => void): () => void;
	/**
	 * Оболочка с 1.0.8: щелчок по уведомлению открывает адрес на месте, через
	 * `window.andresotOpenUrl`, а не перезагружает страницу.
	 */
	opensInPage?: boolean;
}

declare global {
	interface Window {
		andresotDesktop?: DesktopBridge;
		/**
		 * Перейти по своему адресу без перезагрузки. Зовут оболочки (Windows,
		 * Android) по нажатию на уведомление; `true` — открыто, иначе оболочка
		 * загрузит адрес сама. Ставит push.svelte.ts после входа.
		 */
		andresotOpenUrl?: (href: string) => boolean;
	}
}

export const desktopUpdate: DesktopUpdateState = $state({ phase: 'current' });

function hasUnsavedComposerWork(): boolean {
	return (
		document.querySelector('.composer .recording, .composer .attachments, .composer .edit-bar') !==
		null
	);
}

export function initDesktop(): () => void {
	const bridge = window.andresotDesktop;
	if (!bridge) return () => undefined;
	let preparing = false;
	let webUpdating = false;
	let webRetryAfter = 0;
	let preparedVersion = '';
	let prepareRetryAfter = 0;
	const report = () => {
		const busy =
			Boolean(app.activeCall) || Object.keys(app.uploading).length > 0 || hasUnsavedComposerWork();
		bridge.reportBusy(busy);
		if (
			!busy &&
			desktopUpdate.version &&
			desktopUpdate.version !== preparedVersion &&
			Date.now() >= prepareRetryAfter &&
			!preparing &&
			['downloaded', 'deferred'].includes(desktopUpdate.phase)
		)
			void prepareRestart();
		if (
			!busy &&
			!webUpdating &&
			Date.now() >= webRetryAfter &&
			(install.latest || install.updateReady)
		) {
			webUpdating = true;
			webRetryAfter = Date.now() + 60_000;
			void applyDesktopWebUpdate().finally(() => {
				webUpdating = false;
			});
		}
	};
	const prepareRestart = async () => {
		preparing = true;
		const saved = await app.persistForDesktopRestart();
		bridge.readyToRestart(saved);
		if (saved) preparedVersion = desktopUpdate.version ?? '';
		else prepareRetryAfter = Date.now() + 30_000;
		if (!saved)
			Object.assign(desktopUpdate, {
				phase: 'deferred',
				error: 'Не удалось сохранить данные перед обновлением'
			});
		preparing = false;
	};
	const unsubscribe = bridge.onUpdate((state) => {
		Object.assign(desktopUpdate, state);
		if (state.phase === 'preflight') void prepareRestart();
		else if (state.phase === 'downloaded') report();
	});
	void bridge.getInfo().then((info) => {
		if (info?.update) {
			Object.assign(desktopUpdate, info.update);
			report();
		}
	});
	const timer = window.setInterval(report, 1_000);
	window.addEventListener('online', onOnline);
	function onOnline() {
		void window.andresotDesktop?.checkUpdate();
	}
	report();
	return () => {
		unsubscribe();
		clearInterval(timer);
		window.removeEventListener('online', onOnline);
	};
}

export async function applyDesktopWebUpdate(): Promise<void> {
	if (!window.andresotDesktop) return install.applyUpdate();
	while (app.activeCall || Object.keys(app.uploading).length > 0 || hasUnsavedComposerWork()) {
		await new Promise((resolve) => setTimeout(resolve, 1_000));
	}
	if (await app.persistForDesktopRestart()) await install.applyUpdate();
}
